import type { Seg } from '@/engine/math';
import { Rng, hashString } from '@/engine/rng';
import { makeCanvas } from '@/engine/canvas';
import type { FloorKind, WallStyle } from '@/gfx/tiles';
import { TILE } from '@/gfx/tiles';
import { PROPS, DECOR_W, type PropKind } from './props';
import {
  T_VOID, T_WALL, T_FLOOR, T_DOOR, T_STAIRS,
  type FloorDef, type RoomDef, type DoorDef, type DoorKind, type StairsDef, type FloorId, type PropPlacement, type DecorPlacement, type DecorKind, type LightDef, type RoomKind,
} from './types';
import { TEMPLATES, DECOR_TEMPLATES, type FurnishItem } from './furnish';
import { PASSAGE_MOUTHS } from './passages';

export const FLOOR_KINDS: FloorKind[] = ['wood', 'woodPale', 'carpetRed', 'carpetBlue', 'carpetGreen', 'stone', 'checker', 'dirt', 'brick', 'plank', 'marble', 'grassTile'];
export const WALL_STYLES: WallStyle[] = ['green', 'red', 'blue', 'cream', 'panel', 'plaster', 'brick', 'stone'];

export interface DoorEnt {
  id: string;
  x: number; y: number; // tile (for double doors, the west/north tile)
  w: number; // 1 or 2 tiles
  orient: 'h' | 'v';
  kind: DoorKind;
  key?: string;
  trigger?: string;
  label?: string;
  open: boolean;
  locked: boolean;
  /** secret doors are invisible until revealed */
  revealed: boolean;
  rooms: [string, string];
  /** timer for auto-close animation etc. */
  anim: number;
}
export interface StairsEnt {
  def: StairsDef;
  x: number; y: number; w: number; h: number;
  entry: { x: number; y: number }; // floor tile you step from
  far: { x: number; y: number }; // tile that triggers travel
}
export interface PropEnt {
  id: string;
  kind: PropKind;
  x: number; y: number; // tile
  flip: boolean;
  data: Record<string, unknown>;
  occupants: string[];
  searched: boolean;
  /** pushable props: tiles moved so far */
  pushed: number;
  room: string;
}
export interface DecorEnt {
  id: string;
  kind: DecorKind;
  x: number; y: number;
  variant: number;
  data: Record<string, unknown>;
}

export interface Floor {
  id: FloorId;
  def: FloorDef;
  w: number; h: number;
  tiles: Uint8Array;
  floorKind: Uint8Array;
  variant: Uint8Array;
  roomIdx: Int16Array;
  rooms: RoomDef[];
  roomById: Map<string, RoomDef>;
  doors: DoorEnt[];
  doorAt: Map<number, DoorEnt>;
  stairs: StairsEnt[];
  stairsAt: Map<number, StairsEnt>;
  props: PropEnt[];
  decor: DecorEnt[];
  lights: LightDef[];
  /** static solidity (walls + solid props); doors handled separately */
  solid: Uint8Array;
  /** static occluder segments (walls + tall props) */
  segs: Seg[];
  /** walls and doorway geometry only, without furniture */
  wallSegs: Seg[];
  /** created lazily (needs a DOM); see ensureMemory() */
  memory: HTMLCanvasElement;
  /** the ground raster must be rebuilt (furniture moved, secret door revealed) */
  staticDirty: boolean;
}

export function idx(f: { w: number }, x: number, y: number): number {
  return y * f.w + x;
}

export function ensureMemory(f: Floor): HTMLCanvasElement {
  if (!f.memory) f.memory = makeCanvas(f.w * TILE, f.h * TILE);
  return f.memory;
}

export function buildFloor(def: FloorDef, seed: number): Floor {
  const rng = new Rng(seed ^ hashString(def.id));
  const { w, h } = def;
  const n = w * h;
  const f: Floor = {
    id: def.id, def, w, h,
    tiles: new Uint8Array(n),
    floorKind: new Uint8Array(n),
    variant: new Uint8Array(n),
    roomIdx: new Int16Array(n).fill(-1),
    rooms: def.rooms,
    roomById: new Map(def.rooms.map((r) => [r.id, r])),
    doors: [], doorAt: new Map(),
    stairs: [], stairsAt: new Map(),
    props: [], decor: [], lights: [],
    solid: new Uint8Array(n),
    segs: [],
    wallSegs: [],
    memory: null as unknown as HTMLCanvasElement,
    staticDirty: true,
  };

  // rooms
  def.rooms.forEach((r, ri) => {
    if (r.x < 1 || r.y < 1 || r.x + r.w > w - 1 || r.y + r.h > h - 1) throw new Error(`room ${r.id} out of bounds`);
    const fk = FLOOR_KINDS.indexOf(r.floor);
    for (let y = r.y; y < r.y + r.h; y++)
      for (let x = r.x; x < r.x + r.w; x++) {
        const i = idx(f, x, y);
        if (f.tiles[i] === T_FLOOR) throw new Error(`room ${r.id} overlaps ${def.rooms[f.roomIdx[i]].id} at ${x},${y}`);
        f.tiles[i] = T_FLOOR;
        f.floorKind[i] = fk;
        f.variant[i] = rng.int(0, 2);
        f.roomIdx[i] = ri;
      }
  });
  // walls around floors
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const i = idx(f, x, y);
      if (f.tiles[i] !== T_VOID) continue;
      let near = false;
      for (let dy = -1; dy <= 1 && !near; dy++)
        for (let dx = -1; dx <= 1; dx++) {
          const nx = x + dx, ny = y + dy;
          if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
          if (f.tiles[idx(f, nx, ny)] === T_FLOOR) { near = true; break; }
        }
      if (near) f.tiles[i] = T_WALL;
    }

  // doors
  for (const d of def.doors) placeDoor(f, d);
  // stairs
  for (const s of def.stairs) placeStairs(f, s);
  // props
  for (const r of def.rooms) {
    for (const p of r.props ?? []) addProp(f, r, p, true);
  }
  for (const r of def.rooms) {
    if (r.furnish === false) continue;
    furnish(f, r, rng);
    ensureHideSpot(f, r, rng);
  }
  // decor + lights
  for (const p of PASSAGE_MOUTHS.filter(p => p.floor === f.id)) {
    const room = f.roomById.get(p.room);
    if (room) addDecor(f, room, { kind: 'crawlspace', x: p.x, y: p.y, id: `passage:${p.id}` }, rng);
  }
  for (const r of def.rooms) {
    for (const d of r.decor ?? []) addDecor(f, r, d, rng);
    autoDecor(f, r, rng);
    for (const l of r.lights ?? []) f.lights.push(l);
  }
  clearDoorways(f);
  for (const p of f.props) if (p.kind.push) p.data.originX = p.x;
  rebuildStatic(f);
  return f;
}

/**
 * Safety net: no solid prop may stand on a tile a door opens onto (explicit `props` skip the
 * furnishing pass's reservation check, and secret doors move with `at`). Shift the offender along
 * the wall; drop it if nowhere fits. A puzzle's own movable blocker is intentional.
 */
function clearDoorways(f: Floor): void {
  const blocked = new Map<number, Set<string | null>>();
  const block = (i: number, trigger: string | null = null) => {
    const reasons = blocked.get(i) ?? new Set<string | null>();
    reasons.add(trigger); blocked.set(i, reasons);
  };
  for (const d of f.doors) {
    for (let k = 0; k < d.w; k++) {
      const dx = d.x + (d.orient === 'h' ? k : 0), dy = d.y + (d.orient === 'v' ? k : 0);
      const around = d.orient === 'h' ? [[dx, dy - 1], [dx, dy + 1]] : [[dx - 1, dy], [dx + 1, dy]];
      for (const [ax, ay] of around) if (ax >= 0 && ay >= 0 && ax < f.w && ay < f.h) block(idx(f, ax, ay), d.kind === 'secret' ? d.trigger ?? null : null);
    }
  }
  for (const s of f.stairs) block(idx(f, s.entry.x, s.entry.y));
  const covers = (p: PropEnt, i: number) => {
    const x = i % f.w, y = Math.floor(i / f.w);
    return x >= p.x && x < p.x + p.kind.fw && y >= p.y && y < p.y + p.kind.fh;
  };
  const blocksDoorway = (p: PropEnt) => [...blocked].some(([i, reasons]) => covers(p, i) &&
    [...reasons].some(trigger => !p.kind.push || !trigger || trigger !== p.data.trigger));
  for (const p of [...f.props]) {
    if (!p.kind.solid) continue;
    if (!blocksDoorway(p)) continue;
    const room = f.roomById.get(p.room);
    markSolid(f, p, false);
    let placed = false;
    if (room) {
      for (const [ox, oy] of [[1, 0], [-1, 0], [2, 0], [-2, 0], [3, 0], [-3, 0], [0, 1], [0, -1], [4, 0], [-4, 0]]) {
        const nx = p.x + ox, ny = p.y + oy;
        if (!propFootprintFree(f, room, p.kind, nx, ny, false, p)) continue;
        const test = { ...p, x: nx, y: ny };
        if (blocksDoorway(test)) continue;
        p.x = nx; p.y = ny;
        placed = true;
        break;
      }
    }
    if (placed) markSolid(f, p, true);
    else f.props = f.props.filter((q) => q !== p);
  }
}

function roomOf(f: Floor, x: number, y: number): RoomDef | null {
  if (x < 0 || y < 0 || x >= f.w || y >= f.h) return null;
  const ri = f.roomIdx[idx(f, x, y)];
  return ri >= 0 ? f.rooms[ri] : null;
}

function placeDoor(f: Floor, d: DoorDef): void {
  const a = f.roomById.get(d.a);
  if (!a) throw new Error(`door: unknown room ${d.a}`);
  const width = d.kind === 'double' || d.kind === 'arch' || d.kind === 'exit' ? 2 : 1;
  const cands: Array<{ x: number; y: number; orient: 'h' | 'v' }> = [];
  if (d.b === 'outside') {
    // south wall of room a
    const y = a.y + a.h;
    for (let x = a.x; x < a.x + a.w; x++) if (f.tiles[idx(f, x, y)] === T_WALL && f.tiles[idx(f, x, y + 1)] === T_VOID) cands.push({ x, y, orient: 'h' });
  } else {
    const b = f.roomById.get(d.b);
    if (!b) throw new Error(`door: unknown room ${d.b}`);
    for (let y = 1; y < f.h - 1; y++)
      for (let x = 1; x < f.w - 1; x++) {
        if (f.tiles[idx(f, x, y)] !== T_WALL) continue;
        const N = roomOf(f, x, y - 1), S = roomOf(f, x, y + 1), W = roomOf(f, x - 1, y), E = roomOf(f, x + 1, y);
        if ((N === a && S === b) || (N === b && S === a)) cands.push({ x, y, orient: 'h' });
        if ((W === a && E === b) || (W === b && E === a)) cands.push({ x, y, orient: 'v' });
      }
  }
  if (cands.length === 0) throw new Error(`door: rooms ${d.a} and ${d.b} share no wall`);
  // group by orientation & contiguity, pick by `at`
  cands.sort((p, q) => (p.orient === q.orient ? (p.orient === 'h' ? p.x - q.x : p.y - q.y) : p.orient < q.orient ? -1 : 1));
  const orient = cands[0].orient;
  const run = cands.filter((c) => c.orient === orient);
  const at = d.at ?? 0.5;
  let pick = Math.round(at * (run.length - width));
  pick = Math.max(0, Math.min(run.length - width, pick));
  // avoid placing a door tile where another door already is
  for (let tries = 0; tries < run.length; tries++) {
    const ok = Array.from({ length: width }, (_, k) => run[pick + k]).every((c) => c && !f.doorAt.has(idx(f, c.x, c.y)));
    if (ok) break;
    pick = (pick + 1) % Math.max(1, run.length - width + 1);
  }
  const first = run[pick];
  const ent: DoorEnt = {
    id: d.id ?? `${d.a}-${d.b}`,
    x: first.x, y: first.y, w: width, orient, kind: d.kind, key: d.key, trigger: d.trigger, label: d.label,
    open: d.kind === 'open' || d.kind === 'arch',
    locked: d.kind === 'locked' || d.kind === 'boarded' || d.kind === 'cell' || d.kind === 'gate' || d.kind === 'exit' || d.kind === 'bolt',
    revealed: d.kind !== 'secret',
    rooms: [d.a, d.b],
    anim: 0,
  };
  f.doors.push(ent);
  for (let k = 0; k < width; k++) {
    const c = run[pick + k];
    const i = idx(f, c.x, c.y);
    f.tiles[i] = T_DOOR;
    // door tile inherits the floor of room a for rendering
    const ai = idx(f, c.x + (orient === 'v' ? -1 : 0), c.y + (orient === 'h' ? -1 : 0));
    const src = f.tiles[ai] === T_FLOOR ? ai : idx(f, c.x + (orient === 'v' ? 1 : 0), c.y + (orient === 'h' ? 1 : 0));
    f.floorKind[i] = f.floorKind[src];
    f.variant[i] = 0;
    f.roomIdx[i] = f.roomIdx[src];
    f.doorAt.set(i, ent);
  }
}

function placeStairs(f: Floor, s: StairsDef): void {
  const room = f.roomById.get(s.room);
  if (!room) throw new Error(`stairs ${s.id}: unknown room ${s.room}`);
  const w = s.w ?? (s.kind === 'ladder' ? 1 : 2), h = s.h ?? (s.kind === 'ladder' ? 1 : 3);
  const ent: StairsEnt = { def: s, x: s.x, y: s.y, w, h, entry: { x: 0, y: 0 }, far: { x: 0, y: 0 } };
  for (let y = s.y; y < s.y + h; y++)
    for (let x = s.x; x < s.x + w; x++) {
      const i = idx(f, x, y);
      if (f.tiles[i] !== T_FLOOR) throw new Error(`stairs ${s.id} not on floor at ${x},${y}`);
      f.tiles[i] = T_STAIRS;
      f.stairsAt.set(i, ent);
    }
  const cx = s.x + Math.floor((w - 1) / 2);
  const cy = s.y + Math.floor((h - 1) / 2);
  switch (s.enter) {
    case 'S': ent.entry = { x: cx, y: s.y + h }; ent.far = { x: cx, y: s.y }; break;
    case 'N': ent.entry = { x: cx, y: s.y - 1 }; ent.far = { x: cx, y: s.y + h - 1 }; break;
    case 'E': ent.entry = { x: s.x + w, y: cy }; ent.far = { x: s.x, y: cy }; break;
    case 'W': ent.entry = { x: s.x - 1, y: cy }; ent.far = { x: s.x + w - 1, y: cy }; break;
  }
  if (s.kind === 'ladder') ent.far = { x: s.x, y: s.y };
  f.stairs.push(ent);
}

// ---------- props ----------
export function propFootprintFree(f: Floor, room: RoomDef, kind: PropKind, x: number, y: number, allowFlat = false, exclude?: PropEnt): boolean {
  if (x < room.x || y < room.y || x + kind.fw > room.x + room.w || y + kind.fh > room.y + room.h) return false;
  const hits = (p: PropEnt, xx: number, yy: number) => p !== exclude && xx >= p.x && xx < p.x + p.kind.fw && yy >= p.y && yy < p.y + p.kind.fh;
  for (let yy = y; yy < y + kind.fh; yy++)
    for (let xx = x; xx < x + kind.fw; xx++) {
      const i = idx(f, xx, yy);
      if (f.tiles[i] !== T_FLOOR) return false;
      if (f.solid[i] && !(exclude && hits2(exclude, xx, yy))) return false;
      if (!allowFlat && f.props.some((p) => !p.kind.flat && hits(p, xx, yy))) return false;
      if (f.props.some((p) => p.kind.flat === kind.flat && hits(p, xx, yy))) return false;
    }
  return true;
}
function hits2(p: PropEnt, xx: number, yy: number): boolean {
  return xx >= p.x && xx < p.x + p.kind.fw && yy >= p.y && yy < p.y + p.kind.fh;
}

/** tiles that must stay walkable: door approaches and stairs entries (inside this room) */
function reservedTiles(f: Floor, room: RoomDef): Set<number> {
  const set = new Set<number>();
  for (const d of f.doors) {
    for (let k = 0; k < d.w; k++) {
      const dx = d.x + (d.orient === 'h' ? k : 0), dy = d.y + (d.orient === 'v' ? k : 0);
      const around = d.orient === 'h' ? [[dx, dy - 1], [dx, dy + 1], [dx, dy - 2], [dx, dy + 2]] : [[dx - 1, dy], [dx + 1, dy], [dx - 2, dy], [dx + 2, dy]];
      for (const [ax, ay] of around) if (roomOf(f, ax, ay) === room) set.add(idx(f, ax, ay));
    }
  }
  for (const s of f.stairs) {
    if (roomOf(f, s.entry.x, s.entry.y) === room) {
      set.add(idx(f, s.entry.x, s.entry.y));
      // one more tile out from the entry
      const ex = s.entry.x + (s.entry.x - s.far.x > 0 ? 1 : s.entry.x - s.far.x < 0 ? -1 : 0);
      const ey = s.entry.y + (s.entry.y - s.far.y > 0 ? 1 : s.entry.y - s.far.y < 0 ? -1 : 0);
      if (roomOf(f, ex, ey) === room) set.add(idx(f, ex, ey));
    }
  }
  for (const p of PASSAGE_MOUTHS.filter(p => p.floor === f.id && p.room === room.id)) {
    for (let y = p.y + 1; y <= p.y + 3; y++) for (let x = p.x; x < p.x + 2; x++) set.add(idx(f, x, y));
  }
  return set;
}

function addProp(f: Floor, room: RoomDef, p: PropPlacement, strict: boolean): PropEnt | null {
  const kind = PROPS[p.kind];
  if (!kind) throw new Error(`unknown prop kind ${p.kind} in ${room.id}`);
  if (!propFootprintFree(f, room, kind, p.x, p.y)) {
    if (strict) throw new Error(`prop ${p.kind} at ${p.x},${p.y} in ${room.id} does not fit`);
    return null;
  }
  const ent: PropEnt = {
    id: p.id ?? `${room.id}:${p.kind}:${p.x},${p.y}`,
    kind, x: p.x, y: p.y, flip: !!p.flip, data: { ...(p.data ?? {}) }, occupants: [], searched: false, pushed: 0, room: room.id,
  };
  f.props.push(ent);
  markSolid(f, ent, true);
  return ent;
}

export function markSolid(f: Floor, p: PropEnt, on: boolean): void {
  if (!p.kind.solid) return;
  for (let yy = p.y; yy < p.y + p.kind.fh; yy++)
    for (let xx = p.x; xx < p.x + p.kind.fw; xx++) f.solid[idx(f, xx, yy)] = on ? 1 : 0;
}

function roomConnected(f: Floor, room: RoomDef, reserved: Set<number>): boolean {
  const targets = [...reserved].filter((i) => f.tiles[i] === T_FLOOR && !f.solid[i]);
  if (targets.length <= 1) return true;
  const seen = new Set<number>([targets[0]]);
  const q = [targets[0]];
  while (q.length) {
    const i = q.pop()!;
    const x = i % f.w, y = Math.floor(i / f.w);
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const nx = x + dx, ny = y + dy;
      if (roomOf(f, nx, ny) !== room) continue;
      const ni = idx(f, nx, ny);
      if (seen.has(ni) || f.solid[ni] || (f.tiles[ni] !== T_FLOOR && f.tiles[ni] !== T_STAIRS)) continue;
      seen.add(ni);
      q.push(ni);
    }
  }
  return targets.every((t) => seen.has(t));
}

function furnish(f: Floor, room: RoomDef, rng: Rng): void {
  const tpl = TEMPLATES[room.kind];
  if (!tpl) return;
  const reserved = reservedTiles(f, room);
  const items = tpl(room, rng);
  for (const item of items) placeItem(f, room, item, rng, reserved);
}

function placeItem(f: Floor, room: RoomDef, item: FurnishItem, rng: Rng, reserved: Set<number>): PropEnt | null {
  const kind = PROPS[item.kind];
  if (!kind) throw new Error('unknown prop ' + item.kind);
  const cands: Array<[number, number]> = [];
  const push = (x: number, y: number) => cands.push([Math.round(x), Math.round(y)]);
  const t = item.t ?? rng.float(0.15, 0.85);
  const spanX = room.w - kind.fw, spanY = room.h - kind.fh;
  switch (item.at) {
    case 'N': for (const j of jitter(rng)) push(room.x + (t + j) * spanX, room.y); break;
    case 'S': for (const j of jitter(rng)) push(room.x + (t + j) * spanX, room.y + spanY); break;
    case 'W': for (const j of jitter(rng)) push(room.x, room.y + (t + j) * spanY); break;
    case 'E': for (const j of jitter(rng)) push(room.x + spanX, room.y + (t + j) * spanY); break;
    case 'NW': push(room.x, room.y); push(room.x + 1, room.y); push(room.x, room.y + 1); break;
    case 'NE': push(room.x + spanX, room.y); push(room.x + spanX - 1, room.y); push(room.x + spanX, room.y + 1); break;
    case 'SW': push(room.x, room.y + spanY); push(room.x + 1, room.y + spanY); push(room.x, room.y + spanY - 1); break;
    case 'SE': push(room.x + spanX, room.y + spanY); push(room.x + spanX - 1, room.y + spanY); push(room.x + spanX, room.y + spanY - 1); break;
    case 'C': {
      const cx = room.x + spanX / 2 + (item.dx ?? 0), cy = room.y + spanY / 2 + (item.dy ?? 0);
      for (const j of jitter(rng)) push(cx + j * 4, cy + j * 3);
      push(cx, cy);
      break;
    }
    case 'ANY':
      for (let k = 0; k < 12; k++) push(room.x + rng.float(0, spanX), room.y + rng.float(0, spanY));
      break;
  }
  for (const [x, y] of cands) {
    if (!propFootprintFree(f, room, kind, x, y)) continue;
    let hitsReserved = false;
    for (let yy = y; yy < y + kind.fh && !hitsReserved; yy++)
      for (let xx = x; xx < x + kind.fw; xx++) if (reserved.has(idx(f, xx, yy)) && (kind.solid || kind.hide)) { hitsReserved = true; break; }
    if (hitsReserved) continue;
    const ent: PropEnt = {
      id: item.id ?? `${room.id}:${item.kind}:${x},${y}`,
      kind, x, y, flip: rng.chance(0.5) && !!item.flip, data: { ...(item.data ?? {}) }, occupants: [], searched: false, pushed: 0, room: room.id,
    };
    f.props.push(ent);
    markSolid(f, ent, true);
    if (kind.solid && !roomConnected(f, room, reserved)) {
      markSolid(f, ent, false);
      f.props.pop();
      continue;
    }
    return ent;
  }
  return null;
}

/** Rooms where being cornered with nowhere to hide would be unfair. */
const NO_HIDE_KINDS: RoomKind[] = ['corridor', 'passage', 'stairhall', 'tunnel', 'empty', 'balcony', 'well', 'washroom', 'cell', 'closet', 'lair'];
const FALLBACK_HIDES: Record<string, string[]> = {
  hall: ['sofaRed', 'wardrobe'],
  dining: ['sofaRed', 'cabinet'],
  gallery: ['sofa', 'crateStack'],
  trophy: ['sofa', 'crateStack'],
  conservatory: ['crateStack', 'barrel'],
  secret: ['wardrobe', 'crateStack'],
  study: ['sofa', 'wardrobe'],
  library: ['sofa', 'wardrobe'],
  archive: ['crateStack', 'wardrobe'],
  observatory: ['crateStack', 'wardrobe'],
  clock: ['wardrobe', 'crateStack'],
  schoolroom: ['wardrobe', 'crateStack'],
  sewing: ['wardrobe', 'tub'],
  linen: ['tub', 'wardrobe'],
  chapel: ['coffin', 'crateStack'],
};
const DEFAULT_HIDES = ['wardrobe', 'crateStack', 'barrel', 'sofa'];

/** Every room a friend can be cornered in gets at least one hiding place. */
function ensureHideSpot(f: Floor, room: RoomDef, rng: Rng): void {
  if (NO_HIDE_KINDS.includes(room.kind)) return;
  if (f.props.some((p) => p.room === room.id && p.kind.hide)) return;
  const reserved = reservedTiles(f, room);
  const opts = [...(FALLBACK_HIDES[room.kind] ?? []), ...DEFAULT_HIDES];
  for (const kind of opts) {
    const spec = PROPS[kind];
    if (!spec || room.w < spec.fw + 2 || room.h < spec.fh + 2) continue;
    for (const at of ['N', 'W', 'E', 'S', 'ANY'] as const) {
      if (placeItem(f, room, { kind, at }, rng, reserved)) return;
    }
  }
}

function jitter(rng: Rng): number[] {
  const arr = [0, 0.08, -0.08, 0.16, -0.16, 0.24, -0.24, 0.32, -0.32];
  return rng.shuffle(arr.slice(1)).slice(0, 5).concat([0]).sort((a, b) => Math.abs(a) - Math.abs(b));
}

// ---------- decor ----------
function addDecor(f: Floor, room: RoomDef, d: DecorPlacement, rng: Rng): void {
  f.decor.push({ id: d.id ?? `${room.id}:${d.kind}:${d.x},${d.y}`, kind: d.kind, x: d.x, y: d.y, variant: rng.int(0, 4), data: { ...(d.data ?? {}) } });
}

/** Wall tiles along the room's north wall that show a face (i.e. floor directly below). */
export function northWallTiles(f: Floor, room: RoomDef): number[] {
  const out: number[] = [];
  const y = room.y - 1;
  for (let x = room.x; x < room.x + room.w; x++) {
    if (f.tiles[idx(f, x, y)] !== T_WALL) continue;
    if (f.tiles[idx(f, x, y + 1)] !== T_FLOOR) continue; // door/stairs below
    out.push(x);
  }
  return out;
}

function autoDecor(f: Floor, room: RoomDef, rng: Rng): void {
  const wallXs = northWallTiles(f, room);
  const used = new Set<number>();
  for (const d of f.decor) if (d.y === room.y - 1) for (let k = 0; k < DECOR_W[d.kind]; k++) used.add(d.x + k);
  // don't decorate above tall wall-hugging props (they'd cover it)
  for (const p of f.props) if (p.kind.tall && p.y === room.y) for (let k = 0; k < p.kind.fw; k++) used.add(p.x + k);
  const free = () => wallXs.filter((x) => !used.has(x));
  const place = (kind: DecorKind, x: number, data?: Record<string, unknown>) => {
    const w = DECOR_W[kind];
    for (let k = 0; k < w; k++) if (!wallXs.includes(x + k) || used.has(x + k)) return false;
    for (let k = 0; k < w; k++) used.add(x + k);
    f.decor.push({ id: `${room.id}:${kind}:${x}`, kind, x, y: room.y - 1, variant: rng.int(0, 4), data: data ?? {} });
    return true;
  };
  // windows
  if (room.windows && room.windows > 0) {
    const count = Math.max(1, Math.round(room.w * room.windows * 0.16));
    for (let i = 0; i < count; i++) {
      const cand = free();
      if (!cand.length) break;
      const x = cand[Math.floor(((i + 0.5) / count) * cand.length)];
      const big = room.w >= 12 && rng.chance(0.5);
      if (place(big ? 'windowBig' : 'window', x)) {
        const wx = (x + (big ? 1 : 0.5)) * TILE;
        // just inside the room: a light origin left in the wall row would be walled off from the floor
        f.lights.push({ x: wx, y: (room.y + 0.25) * TILE, r: 54 + (big ? 20 : 0), color: '#7d92cf', cone: { angle: Math.PI / 2, half: 0.5 }, id: `moon:${room.id}:${x}` });
      }
    }
  }
  const dtpl = DECOR_TEMPLATES[room.kind];
  if (dtpl) {
    for (const [kind, prob] of dtpl) {
      if (!rng.chance(prob)) continue;
      const cand = free();
      if (!cand.length) break;
      place(kind, rng.pick(cand));
    }
  }
}

// ---------- static data: solid grid & occluders ----------
export function rebuildStatic(f: Floor): void {
  // solid: walls & void
  for (let i = 0; i < f.w * f.h; i++) {
    const t = f.tiles[i];
    if (t === T_WALL || t === T_VOID) f.solid[i] = 1;
    else if (f.solid[i] && !f.props.some((p) => p.kind.solid && i % f.w >= p.x && i % f.w < p.x + p.kind.fw && Math.floor(i / f.w) >= p.y && Math.floor(i / f.w) < p.y + p.kind.fh)) f.solid[i] = 0;
  }
  for (const p of f.props) markSolid(f, p, true);
  // ---- occluders ----
  // North wall faces project upward from the room below. They belong to that room only:
  // from the room above, light stops at the near/top edge instead of lighting the reverse face.
  // Side walls stop at their near edge; no light crosses the thickness of a partition.
  const segs: Seg[] = [];
  const walk = (x: number, y: number) => {
    if (x < 0 || y < 0 || x >= f.w || y >= f.h) return false;
    const t = f.tiles[idx(f, x, y)];
    return t === T_FLOOR || t === T_DOOR || t === T_STAIRS;
  };
  /**
   * Emit merged runs of blocking line. `on` marks cells that carry an edge, `line` is the fixed
   * coordinate of the blocker in tiles, `wallCell` is the wall tile the run hugs (used to seal
   * corners without plugging doorways).
   */
  const emit = (
    horizontal: boolean,
    on: (i: number, j: number) => boolean,
    line: (j: number) => number,
    wallCell: (i: number, j: number) => [number, number],
    nx: number,
    ny: number,
  ) => {
    const outer = horizontal ? f.h : f.w;
    const inner = horizontal ? f.w : f.h;
    for (let j = 0; j <= outer; j++) {
      let start = -1;
      for (let i = 0; i <= inner; i++) {
        const edge = i < inner && on(i, j);
        if (edge && start < 0) start = i;
        if (!edge && start >= 0) {
          // seal corners: grow into a neighbouring wall tile, but never across a doorway
          let a = start, b = i;
          const [px, py] = wallCell(start - 1, j);
          if (!walk(px, py)) a -= 1;
          const [qx, qy] = wallCell(i, j);
          if (!walk(qx, qy)) b += 1;
          const t = line(j) * TILE;
          if (horizontal) segs.push({ ax: a * TILE, ay: t, bx: b * TILE, by: t, nx, ny });
          else segs.push({ ax: t, ay: a * TILE, bx: t, by: b * TILE, nx, ny });
          start = -1;
        }
      }
    }
  };
  // wall above, floor below: blocker at the top of the wall tile (its face belongs to the room below)
  emit(true, (x, y) => y > 0 && walk(x, y) && !walk(x, y - 1), (y) => y - 1, (x, y) => [x, y - 1], 0, 1);
  // floor above, wall below: stop at the near/top edge, before the other room's wall face
  emit(true, (x, y) => y > 0 && walk(x, y - 1) && !walk(x, y), (y) => y, (x, y) => [x, y], 0, -1);
  // floor left, wall right: near/left edge
  emit(false, (y, x) => x > 0 && walk(x - 1, y) && !walk(x, y), (x) => x, (y, x) => [x, y], -1, 0);
  // wall left, floor right: near/right edge
  emit(false, (y, x) => x > 0 && walk(x, y) && !walk(x - 1, y), (x) => x, (y, x) => [x - 1, y], 1, 0);
  // walls only: used for "can this character reach that thing" checks
  f.wallSegs = segs.slice();
  for (const p of f.props) {
    if (!p.kind.tall) continue;
    const x0 = p.x * TILE, y0 = p.y * TILE, x1 = (p.x + p.kind.fw) * TILE, y1 = (p.y + p.kind.fh) * TILE;
    // shrink a little so light grazes past edges nicely
    segs.push({ ax: x0 + 1, ay: y0 + 2, bx: x1 - 1, by: y0 + 2 });
    segs.push({ ax: x0 + 1, ay: y1 - 1, bx: x1 - 1, by: y1 - 1 });
    segs.push({ ax: x0 + 1, ay: y0 + 2, bx: x0 + 1, by: y1 - 1 });
    segs.push({ ax: x1 - 1, ay: y0 + 2, bx: x1 - 1, by: y1 - 1 });
  }
  f.segs = segs;
  f.staticDirty = true;
}

export function tileAt(f: Floor, x: number, y: number): number {
  if (x < 0 || y < 0 || x >= f.w || y >= f.h) return T_VOID;
  return f.tiles[idx(f, x, y)];
}
export function roomAt(f: Floor, x: number, y: number): RoomDef | null {
  return roomOf(f, x, y);
}
export function roomAtPx(f: Floor, px: number, py: number): RoomDef | null {
  return roomOf(f, Math.floor(px / TILE), Math.floor(py / TILE));
}

export type { RoomKind };
